import { View } from "react-native";
import { COMMENTS } from "src/api/featureFlags";
import PhotoState from "src/api/graphql/__generated__/enums/PhotoState";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import Loading from "src/components/Loading";
import OpacityView from "src/components/OpacityView";
import PillButton from "src/components/PillButton";
import { StickerRowItem } from "src/components/StickerRow";
import { ParsedStickerRowItem } from "src/components/StickerRow/types";
import VideoDurationDisplay from "src/components/VideoDurationDisplay";
import { useGatheringStore } from "src/contexts/GatheringContext";
import type { GatheringOrLocalPhoto } from "src/hooks/useGatheringPhotosWithLocal";
import IconFire from "src/icons/IconFire";
import IconLivePhoto from "src/icons/IconLivePhoto";
import IconMaximize from "src/icons/IconMaximize";
import { paddingMedium, paddingSmall } from "src/styles/spacing";
import { Pill } from "src/swsh-native";
import PhotoInfoBlurbDisplay from "../../../../../../../../components/PhotoInfoBlurbDisplay/PhotoInfoBlurbDisplay";
import usePresentPhoto from "../../../../hooks/usePresentPhoto";
import GatheringHomePhotoCommentDisplay from "./components/GatheringHomePhotoCommentDisplay";
import ReactionDisplay from "./components/ReactionDisplay";

const GatheringHomePhotoOverlay = ({
	photo,
	sticker,
	borderRadius,
}: {
	photo: GatheringOrLocalPhoto;
	sticker: ParsedStickerRowItem | null;

	borderRadius: number;
}) => {
	const selectMode = useGatheringStore((state) => state.selectMode);
	const presentPhoto = usePresentPhoto();
	const isTopPhoto = photo?.isTopPhoto ?? false;
	const isActivePhoto = photo.state === PhotoState.Active;

	return (
		<>
			<>
				{/* Top right */}
				{photo.variant === PhotoVariant.Video && photo.videoDuration != null && (
					<VideoDurationDisplay
						duration={photo.videoDuration}
						style={[
							{
								top: 0,
								right: 0,
								position: "absolute",
								borderTopRightRadius: borderRadius,
							},
						]}
					/>
				)}
				{selectMode === "Select" && (
					<PillButton
						opaque
						containerStyle={{
							position: "absolute",
							top: paddingMedium,
							right: paddingMedium,
						}}
						onPress={() => {
							presentPhoto({ photoId: photo.photoId });
						}}
						Icon={IconMaximize}
					/>
				)}
			</>
			<>
				{/* Top left */}
				{(photo.state === PhotoState.Preparing ||
					photo.state === PhotoState.Hidden ||
					photo.state === PhotoState.Errored ||
					photo.variant === PhotoVariant.LivePhoto ||
					isTopPhoto) && (
					<View
						pointerEvents="none"
						style={{
							position: "absolute",
							top: paddingMedium,
							left: paddingMedium,
							flexDirection: "row",
							columnGap: paddingSmall,
							alignItems: "center",
						}}
					>
						{photo.state === PhotoState.Preparing && <Loading testID="Preparing" />}
						{(photo.state === PhotoState.Hidden ||
							photo.state === PhotoState.Errored) && (
							<PhotoInfoBlurbDisplay photo={photo} sizeVariant="sm" />
						)}
						{isActivePhoto && isTopPhoto && (
							<Pill testID="TopPhoto" Icon={IconFire} blur />
						)}
						{isActivePhoto && photo.variant === PhotoVariant.LivePhoto && (
							<Pill Icon={IconLivePhoto} blur />
						)}
					</View>
				)}
			</>
			<>
				{/* Bottom left */}
				{Boolean(photo.numPrimaryReaction || photo.numComments) && (
					<OpacityView
						style={{
							position: "absolute",
							bottom: paddingMedium,
							left: paddingMedium,
							flexDirection: "row",
							columnGap: paddingSmall,
							alignItems: "center",
						}}
					>
						{COMMENTS &&
							photo.numComments != null &&
							photo.numComments > 0 &&
							isActivePhoto && (
								<GatheringHomePhotoCommentDisplay numComments={photo.numComments} />
							)}
						{photo.numPrimaryReaction != null &&
							photo.numPrimaryReaction > 0 &&
							isActivePhoto && (
								<ReactionDisplay numPrimaryReaction={photo.numPrimaryReaction} />
							)}
					</OpacityView>
				)}
			</>
			<StickerRowItem sticker={sticker} />
		</>
	);
};
export default GatheringHomePhotoOverlay;
